import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { ThemeProvider } from "@/contexts/ThemeContext";
import { setTabToken, setBearerToken } from "@/lib/api";
// InstallAppPrompt popup removed — replaced by inline install cards embedded on
// the landing page and the staff login portal.
import { Toaster } from "@/components/ui/sonner";
import ConfirmDialogProvider from "@/components/ui/confirm-dialog";
import SiteGate from "@/components/SiteGate";
import OrdersPausedBanner from "@/components/OrdersPausedBanner";
import { SessionTimeoutAlert } from "@/components/HIPAACompliance";
import ThemeSync from "@/components/ThemeSync";
import { isNativeApp } from "@/lib/native";

import LandingPage from "@/pages/LandingPage";
import CoaPublic from "@/pages/CoaPublic";
import CoaShortLink from "@/pages/CoaShortLink";
import PatientLogin from "@/pages/PatientLogin";
import PatientRegister from "@/pages/PatientRegister";
import PatientDashboard from "@/pages/PatientDashboard";
import PatientProfile from "@/pages/PatientProfile";
import MedicalQuestionnaire from "@/pages/MedicalQuestionnaire";
import MedicationSelection from "@/pages/MedicationSelection";
import PaymentSuccess from "@/pages/PaymentSuccess";
import PaymentCancel from "@/pages/PaymentCancel";
import PatientDocuments from "@/pages/PatientDocuments";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import VerifyEmailChange from "@/pages/VerifyEmailChange";
import PepFloRedirect from "@/pages/PepFloRedirect";
import PatientOnboarding from "@/pages/PatientOnboarding";
import PrescriberLogin from "@/pages/PrescriberLogin";
import PrescriberDashboard from "@/pages/PrescriberDashboard";
import AdminDashboard from "@/pages/AdminDashboard";
import ITDashboard from "@/pages/ITDashboard";
import ErrorBoundary from "@/components/ErrorBoundary";
import PharmacyInfoPage from "@/pages/PharmacyInfoPage";
// MedicationsInfoPage retired in favor of /pricing — kept as 301-style redirect.
import StaffChatPopoutPage from "@/pages/StaffChatPopoutPage";
import ReferralsPage from "@/pages/ReferralsPage";
import CompanionApp from "@/pages/CompanionApp";
import EsignPage from "@/pages/EsignPage";
import PatientHelpGuide from "@/components/PatientHelpGuide";
import PatientChatWidget from "@/components/PatientChatWidget";
import FloatingLanguageToggle from "@/components/FloatingLanguageToggle";
import { BottomRightStackProvider } from "@/components/BottomRightStack";
import ScrollToTop from "@/components/ScrollToTop";
import { BreakGlassBanner } from "@/components/BreakGlassControls";
import SeoRobotsGate from "@/components/SeoRobotsGate";
import PrivacyPolicyPage from "@/pages/PrivacyPolicyPage";
import UnsubscribePage from "@/pages/UnsubscribePage";
import HipaaCompliancePage from "@/pages/HipaaCompliancePage";
import TermsOfServicePage from "@/pages/TermsOfServicePage";
import ShippingStudyPage from "@/pages/ShippingStudyPage";
import FAQPage from "@/pages/FAQPage";
import PricingPage from "@/pages/PricingPage";
import CompanionInstallPage from "@/pages/CompanionInstallPage";
import GettingStartedWatchPage from "@/pages/GettingStartedWatchPage";
import PharmacyAffiliatePage from "@/pages/PharmacyAffiliatePage";
import ReviewChangeRequest from "@/pages/ReviewChangeRequest";
import OnlineTrackingDisclosurePage from "@/pages/OnlineTrackingDisclosurePage";
import ShareYourStoryPage from "@/pages/ShareYourStoryPage";
import BrowseMedicationsPage from "@/pages/BrowseMedicationsPage";
import QaResolvePage from "@/pages/QaResolvePage";
import BenefitsVerifyPage from "@/pages/BenefitsVerifyPage";
import StateLandingPage from "@/pages/StateLandingPage";
import CompetitorComparePage from "@/pages/CompetitorComparePage";
import FactStorageTemperaturesPage from "@/pages/FactStorageTemperaturesPage";
import FactCompoundedVsBrandedPage from "@/pages/FactCompoundedVsBrandedPage";
import WhereToOrderGlp1Page from "@/pages/WhereToOrderGlp1Page";
import PricingMegaComparePage from "@/pages/PricingMegaComparePage";
import PressPage from "@/pages/PressPage";
import CookieBanner from "@/components/CookieBanner";
import MarketingRender from "@/pages/MarketingRender";
import StepathonPage from "@/pages/StepathonPage";
import StepathonEventPage from "@/pages/StepathonEventPage";
import StepathonRewardsPage from "@/pages/StepathonRewardsPage";

// Mobile -> Web session bootstrap.
// The Expo Checkout screen opens a WebView pointed at
// `/medications?mobile_token=<jwt>&mobile_tab=<tab_token>`. We hydrate BOTH
// the JWT (as an Authorization: Bearer header) AND the tab_token at module-
// load time (BEFORE React renders, before AuthProvider fires its first
// /auth/me) so the WebView is authenticated on the very first request.
// Without this, patients get bounced to /login -> "sign-in loop" reported
// on TestFlight.
(function bootstrapMobileToken() {
  if (typeof window === "undefined") return;
  try {
    const params = new URLSearchParams(window.location.search);
    const mobileTok = params.get("mobile_token");
    const mobileTab = params.get("mobile_tab");
    let changed = false;
    if (mobileTok && mobileTok.length > 10) {
      // JWT — used as the Authorization: Bearer header. The web api client
      // reads this key from sessionStorage on every request.
      setBearerToken(mobileTok);
      params.delete("mobile_token");
      changed = true;
    }
    if (mobileTab && mobileTab.length > 5) {
      setTabToken(mobileTab);
      params.delete("mobile_tab");
      changed = true;
    }
    if (changed) {
      const cleaned = params.toString();
      const newUrl = window.location.pathname + (cleaned ? `?${cleaned}` : "") + window.location.hash;
      window.history.replaceState({}, "", newUrl);
    }
  } catch { /* ignore malformed query strings */ }
})();

function ProtectedRoute({ children, allowedRoles }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 border-3 border-purple-600 border-t-transparent rounded-full animate-spin" />
          <p className="text-sm text-slate-500">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) return <Navigate to="/login" replace />;
  if (allowedRoles && !allowedRoles.includes(user.role)) {
    if (user.role === "admin") return <Navigate to="/admin" replace />;
    if (user.role === "it") return <Navigate to="/it" replace />;
    if (user.role === "prescriber" || user.role === "customer_service") return <Navigate to="/prescriber" replace />;
    return <Navigate to="/dashboard" replace />;
  }

  return children;
}

function PublicRoute({ children }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="w-8 h-8 border-3 border-purple-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (user) {
    if (user.role === "admin") return <Navigate to="/admin" replace />;
    if (user.role === "it") return <Navigate to="/it" replace />;
    if (user.role === "prescriber" || user.role === "customer_service") return <Navigate to="/prescriber" replace />;
    // Inside the native Companion app, patients boot straight into the
    // Companion instead of the full web dashboard.
    return <Navigate to={isNativeApp() ? "/companion" : "/dashboard"} replace />;
  }

  return children;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/quiz" element={<LandingPage />} />
      <Route path="/stepathon" element={<StepathonPage />} />
      <Route path="/stepathon/rewards" element={<StepathonRewardsPage />} />
      <Route path="/stepathon/:slug" element={<StepathonEventPage />} />
      <Route path="/coa" element={<CoaPublic />} />
      <Route path="/c/:id" element={<CoaShortLink />} />
      <Route path="/pepflo" element={<PepFloRedirect />} />
      <Route path="/esign/:token" element={<EsignPage />} />
      <Route path="/login" element={<PublicRoute><PatientLogin /></PublicRoute>} />
      <Route path="/register" element={<PublicRoute><PatientRegister /></PublicRoute>} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/verify-email-change" element={<VerifyEmailChange />} />
      {/* New canonical staff login URL. */}
      <Route path="/cliniclogin" element={<PublicRoute><PrescriberLogin /></PublicRoute>} />
      {/* TODO(GLPFlo): legacy `/doctordoctor` staff URL — kept temporarily so old
          bookmarks keep working. Once the team has updated their bookmarks /
          training docs, remove this Route + the matching entries in
          STAFF_PATHS, AuthContext.js, ThemeContext.js, api.js, InstallApp*.js,
          and trainingContent.js. (Search the repo for `doctordoctor`.) */}
      <Route path="/doctordoctor" element={<PublicRoute><PrescriberLogin /></PublicRoute>} />
      <Route path="/dashboard" element={<ProtectedRoute allowedRoles={["patient"]}><ErrorBoundary label="Your dashboard"><PatientDashboard /></ErrorBoundary></ProtectedRoute>} />
      <Route path="/onboarding" element={<ProtectedRoute allowedRoles={["patient"]}><PatientOnboarding /></ProtectedRoute>} />
      <Route path="/profile" element={<ProtectedRoute allowedRoles={["patient"]}><PatientProfile /></ProtectedRoute>} />
      <Route path="/questionnaire" element={<ProtectedRoute allowedRoles={["patient"]}><MedicalQuestionnaire /></ProtectedRoute>} />
      <Route path="/medications" element={<ProtectedRoute allowedRoles={["patient"]}><MedicationSelection /></ProtectedRoute>} />
      <Route path="/documents" element={<ProtectedRoute allowedRoles={["patient"]}><PatientDocuments /></ProtectedRoute>} />
      <Route path="/payment/success" element={<ProtectedRoute allowedRoles={["patient"]}><PaymentSuccess /></ProtectedRoute>} />
      <Route path="/payment/cancel" element={<PaymentCancel />} />
      <Route path="/prescriber" element={<ProtectedRoute allowedRoles={["prescriber", "admin", "customer_service"]}><ErrorBoundary label="The clinic portal"><PrescriberDashboard /></ErrorBoundary></ProtectedRoute>} />
      <Route path="/admin" element={<ProtectedRoute allowedRoles={["admin"]}><ErrorBoundary label="The admin dashboard"><AdminDashboard /></ErrorBoundary></ProtectedRoute>} />
      <Route path="/it" element={<ProtectedRoute allowedRoles={["it"]}><ErrorBoundary label="The IT triage console"><ITDashboard /></ErrorBoundary></ProtectedRoute>} />
      <Route path="/staff-chat" element={<ProtectedRoute allowedRoles={["admin", "prescriber", "customer_service", "it", "super_admin"]}><StaffChatPopoutPage /></ProtectedRoute>} />
      <Route path="/referrals" element={<ProtectedRoute allowedRoles={["patient"]}><ReferralsPage /></ProtectedRoute>} />
      <Route path="/companion" element={<ProtectedRoute allowedRoles={["patient"]}><CompanionApp /></ProtectedRoute>} />
      <Route path="/companion/:tab" element={<ProtectedRoute allowedRoles={["patient"]}><CompanionApp /></ProtectedRoute>} />
      <Route path="/pharmacy-info" element={<PharmacyInfoPage />} />
      <Route path="/medications-info" element={<Navigate to="/pricing" replace />} />
      <Route path="/our-physicians" element={<Navigate to="/" replace />} />
      <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
      <Route path="/privacy" element={<PrivacyPolicyPage />} />
      <Route path="/unsubscribe" element={<UnsubscribePage />} />
      <Route path="/u" element={<UnsubscribePage />} />
      <Route path="/hipaa-compliance" element={<HipaaCompliancePage />} />
      <Route path="/compliance" element={<HipaaCompliancePage />} />
      <Route path="/online-tracking" element={<OnlineTrackingDisclosurePage />} />
      <Route path="/results" element={<Navigate to="/" replace />} />
      <Route path="/share-your-story/:userId/:token" element={<ShareYourStoryPage />} />
      <Route path="/s/:userId/:token" element={<ShareYourStoryPage />} />
      <Route path="/browse-medications" element={<BrowseMedicationsPage />} />
      <Route path="/terms-of-service" element={<TermsOfServicePage />} />
      <Route path="/shipping-study" element={<ShippingStudyPage />} />
      <Route path="/faq" element={<FAQPage />} />
      <Route path="/pricing" element={<PricingPage />} />
      <Route path="/companion/install" element={<CompanionInstallPage />} />
      <Route path="/watch/getting-started" element={<GettingStartedWatchPage />} />
      <Route path="/pharmacy-affiliate" element={<PharmacyAffiliatePage />} />
      <Route path="/qa/resolve/:cid" element={<QaResolvePage />} />
      <Route path="/verify/:token" element={<BenefitsVerifyPage />} />
      <Route path="/orders/:orderId/review-change" element={<ProtectedRoute allowedRoles={["patient"]}><ReviewChangeRequest /></ProtectedRoute>} />
      {/*
        Hidden SEO / AI-bot landing pages (Phase 2 + 3). Routes are
        intentionally NOT linked from the Navbar, Hamburger Menu, or
        Footer — discoverability is via /sitemap.xml, /llms.txt, and
        direct search/AI-assistant referrals. See /app/memory/PRD.md.
      */}
      <Route path="/glp1-telehealth/:state" element={<StateLandingPage />} />
      <Route path="/vs/:competitor" element={<CompetitorComparePage />} />
      <Route path="/facts/glp1-storage-temperatures" element={<FactStorageTemperaturesPage />} />
      <Route path="/facts/compounded-vs-branded-semaglutide" element={<FactCompoundedVsBrandedPage />} />
      <Route path="/where-to-order-glp1-online" element={<WhereToOrderGlp1Page />} />
      <Route path="/pricing-vs-ro-vs-hers-vs-henry-meds" element={<PricingMegaComparePage />} />
      <Route path="/press" element={<PressPage />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function PatientHelpGuideForPatients() {
  // Only render the help guide for logged-in patients (not staff).
  const { user } = useAuth();
  if (!user || user.role !== "patient") return null;
  return <PatientHelpGuide />;
}

function AppWrapper() {
  // Skip site gate for staff-only routes AND public marketing/SEO pages so
  // potential patients can browse the catalog and pricing without entering
  // the beta site-gate password. Staff have their own auth; marketing pages
  // hold zero PHI and are safe to expose.
  //
  // Read pathname ONCE at mount (not via `useLocation`) — otherwise every
  // SPA navigation flips `skipSiteGate` and remounts the entire app tree
  // (SiteGate wrapper toggles), which both kills perf and can cascade
  // into "Maximum update depth" loops through provider/hook deps.
  const path = typeof window !== "undefined" ? window.location.pathname : "";
  // Staff routes that bypass the site-wide password gate. NOTE: `/cliniclogin`
  // and `/doctordoctor` (the legacy staff login URL) are INTENTIONALLY NOT
  // on this list — staff must enter the site password the same way patients
  // do. The post-auth staff routes below stay on the list so an admin whose
  // localStorage was cleared mid-session isn't re-prompted before they can
  // re-authenticate.
  const STAFF_PATHS = ["/admin", "/prescriber"];
  const PUBLIC_PATHS = [
    "/browse-medications",
    "/pricing",
    "/medications-info", // legacy redirect
    "/terms-of-service",
    "/privacy-policy",
    "/shipping-study",
    "/about",
    "/faq",
    "/contact",
    "/qa/resolve",  // magic-link QA resolution page — token-auth only, must bypass site gate
    "/verify",      // magic-link benefits-verification page — token-auth only, must bypass site gate
    // Hidden SEO / AI-bot pages — orphan routes, no nav links, but must
    // be crawlable by Google/ChatGPT/Claude/Perplexity for organic + AI
    // discoverability. They contain zero PHI.
    "/glp1-telehealth",
    "/vs",
    "/facts",
  ];
  const matchesPath = (paths) => paths.some((p) => path === p || path.startsWith(p + "/"));
  const isStaffRoute = matchesPath(STAFF_PATHS);
  const isPublicRoute = matchesPath(PUBLIC_PATHS);
  // The installed native Companion app never sees the beta access-code gate —
  // testers/patients open straight into the app experience.
  const skipSiteGate = isStaffRoute || isPublicRoute || isNativeApp();

  useEffect(() => {
    if (isNativeApp()) document.body.classList.add("native-app");
  }, []);
  const content = (
    <ThemeProvider>
      <AuthProvider>
        <BottomRightStackProvider>
          <ThemeSync />
          <ScrollToTop />
          <SeoRobotsGate />
          <OrdersPausedBanner />
          <BreakGlassBanner />
          <AppRoutes />
          <PatientHelpGuideForPatients />
          {/* Patient chat widget — mounted globally so the bot is reachable
              from EVERY page (logged-out marketing pages included). The
              widget self-hides on staff portals via the role check inside
              PatientChatWidget. Anonymous visitors get a bot-only session
              with no PHI tooling and no SMS escalation. */}
          <PatientChatWidget />
          <FloatingLanguageToggle />
          <CookieBanner />
          <Toaster position="top-right" richColors />
          <ConfirmDialogProvider />
          <SessionTimeoutAlert />
        </BottomRightStackProvider>
      </AuthProvider>
    </ThemeProvider>
  );
  return skipSiteGate ? content : <SiteGate>{content}</SiteGate>;
}

function App() {
  // Isolated marketing render harness — bypass ALL layout wrappers
  // (Navbar, Footer, SiteGate, Toasters, ChatWidget). Used by Playwright
  // headless capture to produce paid-social animated ads with nothing
  // but the ad creative on the canvas.
  const path = typeof window !== "undefined" ? window.location.pathname : "";
  if (path.startsWith("/render-marketing")) {
    return (
      <BrowserRouter>
        <Routes>
          <Route path="/render-marketing/:variant/:aspect" element={<MarketingRender />} />
          <Route path="/render-marketing/:variant" element={<MarketingRender />} />
          <Route path="/render-marketing" element={<MarketingRender />} />
        </Routes>
      </BrowserRouter>
    );
  }
  return (
    <BrowserRouter>
      <AppWrapper />
    </BrowserRouter>
  );
}

export default App;
